'use client'

import { ReactNode, startTransition, useEffect, useRef, useState } from 'react'

const DELAY = 50

// Use events that fire AFTER interactions complete, so they don't interfere with clicks/taps
// This ensures clicks, taps, and keyboard input work normally
const EVENT_LIST = ['mouseup', 'mousemove', 'wheel', 'pointermove', 'touchend']

/**
 * HOC for rendering a component only when the browser is idle.
 * Uses requestIdleCallback API to defer rendering until the browser has free time.
 */
export const RenderOnFirstInteraction = ({
	element,
	fallback = null,
}: {
	element: ReactNode
	fallback?: ReactNode
}) => {
	const [show, setShow] = useState(false)

	const timers = useRef<number[]>([])

	useEffect(() => {
		if (show) {
			return
		}

		const run = () => {
			// Schedule after paint + tiny cushion to avoid colliding with the input handler’s work
			requestAnimationFrame(() => {
				const id = window.setTimeout(() => {
					startTransition(() => setShow(true))
				}, DELAY)
				timers.current.push(id)
			})
		}

		const onFirstInput = () => {
			detach()
			run()
		}

		const opts: AddEventListenerOptions = {
			once: true,
			passive: true,
			capture: true,
		}
		EVENT_LIST.forEach((ev) => window.addEventListener(ev, onFirstInput, opts))

		const detach = () => {
			EVENT_LIST.forEach((event) =>
				window.removeEventListener(event, onFirstInput, opts),
			)
			timers.current.forEach((id) => clearTimeout(id))
			timers.current = []
		}

		return detach
	}, [show])

	return show ? element : fallback
}
